import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, Zap, Phone, CheckCircle, AlertTriangle, Info, Battery, Car, MapPin, CreditCard } from 'lucide-react';
import useSEO from '../../hooks/useSEO';

const BateriaDescargadaCocheAlquiler = () => {
  const navigate = useNavigate();

  useSEO({
    title: 'Batería Descargada Coche de Alquiler Barcelona | Qué Hacer | ArrancaYa',
    description: 'Tu coche de alquiler no arranca en Barcelona? Guía completa sobre qué hacer cuando la batería del coche de alquiler se descarga. Servicio 24/7, llegamos en 30min.',
    canonical: 'https://arrancaya-bcn.es/blog/bateria-descargada-coche-alquiler'
  });

  useEffect(() => {
    const metaKeywords = document.querySelector('meta[name="keywords"]') || document.createElement('meta');
    metaKeywords.name = 'keywords';
    metaKeywords.content = 'batería descargada coche alquiler, coche alquiler no arranca, batería coche rental Barcelona, emergencia coche alquiler, arranque coche alquiler';
    document.head.appendChild(metaKeywords);

    const schema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Batería Descargada en Coche de Alquiler: Qué Hacer en Barcelona",
      "description": "Guía completa sobre qué hacer cuando la batería de tu coche de alquiler se descarga en Barcelona",
      "author": {
        "@type": "Organization",
        "name": "ArrancaYa BCN"
      },
      "datePublished": "2026-01-20",
      "dateModified": "2026-01-20"
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'page_view',
      'page_title': 'Batería Descargada Coche Alquiler',
      'page_location': window.location.href,
      'content_group1': 'Blog',
      'content_group2': 'Coche Alquiler'
    });

    return () => {
      document.head.removeChild(script);
      if (metaKeywords.parentNode) {
        document.head.removeChild(metaKeywords);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-obsidian">
      {/* Navigation */}
      <div className="bg-obsidian/90 backdrop-blur-xl border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link 
            to="/blog" 
            className="inline-flex items-center text-neon hover:text-neon/80 font-medium transition-colors group"
            data-testid="back-to-blog-link"
          >
            <ArrowLeft className="h-4 w-4 mr-2 transition-transform group-hover:-translate-x-1" />
            Volver al Blog
          </Link>
        </div>
      </div>

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <header className="mb-12">
          <div className="flex items-center space-x-4 text-sm text-white/60 mb-6">
            <span className="bg-neon/20 text-neon px-3 py-1 rounded-sm font-medium uppercase tracking-wider text-xs">Guía Práctica</span>
            <span>•</span>
            <span>20 de enero, 2026</span>
            <span>•</span>
            <span>6 min de lectura</span>
          </div>
          
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black text-white mb-8 tracking-tighter leading-tight">
            Batería Descargada en
            <span className="text-stark"> Coche de Alquiler</span>
          </h1>
          
          <div className="bg-white/5 border border-white/10 rounded-sm p-8 mb-8">
            <p className="text-xl text-white/80 leading-relaxed">
              <strong className="text-white">¿Tu coche de alquiler no arranca en Barcelona?</strong> No te preocupes. 
              Te explicamos paso a paso qué hacer, a quién llamar y cómo resolver el problema rápidamente sin complicaciones con la empresa de alquiler.
            </p>
          </div>
        </header>

        {/* Content */}
        <div className="space-y-12">
          
          {/* Sección 1: No entres en pánico */}
          <section className="bg-white/5 border border-white/10 rounded-sm p-8">
            <h2 className="font-heading text-2xl font-bold text-white mb-6 flex items-center">
              <span className="bg-neon text-obsidian rounded-sm w-10 h-10 flex items-center justify-center mr-4 text-lg font-black">1</span>
              Lo Primero: No Entres en Pánico
            </h2>
            <div className="space-y-4 text-white/80">
              <p className="leading-relaxed">
                Que la batería de un coche de alquiler se descargue es <strong className="text-white">más común de lo que piensas</strong>. 
                Puede pasar por dejar las luces encendidas, el frío, o simplemente porque la batería ya estaba en mal estado.
              </p>
              <div className="bg-neon/10 border border-neon/30 rounded-sm p-4">
                <p className="text-neon font-medium">
                  Importante: Esto NO es tu culpa y NO tendrás que pagar daños a la empresa de alquiler si sigues los pasos correctos.
                </p>
              </div>
            </div>
          </section>

          {/* Sección 2: Opciones */}
          <section className="bg-white/5 border border-white/10 rounded-sm p-8">
            <h2 className="font-heading text-2xl font-bold text-white mb-6 flex items-center">
              <span className="bg-neon text-obsidian rounded-sm w-10 h-10 flex items-center justify-center mr-4 text-lg font-black">2</span>
              Tus Opciones en Barcelona
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Opción A */}
              <div className="bg-stark/10 border border-stark/30 rounded-sm p-6">
                <h3 className="font-heading text-lg font-bold text-stark mb-3 flex items-center">
                  <AlertTriangle className="h-5 w-5 mr-2" />
                  Llamar a la Empresa de Alquiler
                </h3>
                <ul className="text-white/70 space-y-2 text-sm">
                  <li className="flex items-start">
                    <span className="text-stark mr-2">✗</span>
                    Tiempo de espera: 1-3 horas
                  </li>
                  <li className="flex items-start">
                    <span className="text-stark mr-2">✗</span>
                    Call center con largas esperas
                  </li>
                  <li className="flex items-start">
                    <span className="text-stark mr-2">✗</span>
                    Pueden mandarte una grúa (más demora)
                  </li>
                  <li className="flex items-start">
                    <span className="text-stark mr-2">✗</span>
                    Posibles cargos adicionales
                  </li>
                </ul>
              </div>

              {/* Opción B */}
              <div className="bg-neon/10 border border-neon/30 rounded-sm p-6">
                <h3 className="font-heading text-lg font-bold text-neon mb-3 flex items-center">
                  <Zap className="h-5 w-5 mr-2" />
                  Llamar a ArrancaYa BCN
                </h3>
                <ul className="text-white/70 space-y-2 text-sm">
                  <li className="flex items-start">
                    <span className="text-neon mr-2">✓</span>
                    Llegamos en menos de 30 minutos
                  </li>
                  <li className="flex items-start">
                    <span className="text-neon mr-2">✓</span>
                    Precio fijo: 69€ día / 99€ noche
                  </li>
                  <li className="flex items-start">
                    <span className="text-neon mr-2">✓</span>
                    Solo pagas si tu coche arranca
                  </li>
                  <li className="flex items-start">
                    <span className="text-neon mr-2">✓</span>
                    Te damos factura para reclamar
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Sección 3: Por qué nosotros */}
          <section className="bg-white/5 border border-white/10 rounded-sm p-8">
            <h2 className="font-heading text-2xl font-bold text-white mb-6 flex items-center">
              <span className="bg-neon text-obsidian rounded-sm w-10 h-10 flex items-center justify-center mr-4 text-lg font-black">3</span>
              ¿Por Qué Llamar a un Servicio Externo?
            </h2>
            
            <div className="space-y-4 text-white/80">
              <p className="leading-relaxed">
                Muchos turistas y residentes piensan que <strong className="text-white">deben llamar obligatoriamente a la empresa de alquiler</strong>. 
                Pero la realidad es diferente:
              </p>
              
              <div className="grid md:grid-cols-3 gap-4 mt-6">
                <div className="bg-obsidian border border-white/10 rounded-sm p-4 text-center">
                  <Car className="h-8 w-8 text-neon mx-auto mb-3" />
                  <h4 className="font-bold text-white mb-2">Tu Derecho</h4>
                  <p className="text-sm text-white/60">Puedes usar cualquier servicio de asistencia en carretera</p>
                </div>
                <div className="bg-obsidian border border-white/10 rounded-sm p-4 text-center">
                  <CreditCard className="h-8 w-8 text-neon mx-auto mb-3" />
                  <h4 className="font-bold text-white mb-2">Reembolsable</h4>
                  <p className="text-sm text-white/60">Guarda la factura para reclamar a la empresa de alquiler</p>
                </div>
                <div className="bg-obsidian border border-white/10 rounded-sm p-4 text-center">
                  <MapPin className="h-8 w-8 text-neon mx-auto mb-3" />
                  <h4 className="font-bold text-white mb-2">Más Rápido</h4>
                  <p className="text-sm text-white/60">Servicio local en Barcelona, sin intermediarios</p>
                </div>
              </div>
            </div>
          </section>

          {/* Sección 4: Paso a paso */}
          <section className="bg-white/5 border border-white/10 rounded-sm p-8">
            <h2 className="font-heading text-2xl font-bold text-white mb-6 flex items-center">
              <span className="bg-neon text-obsidian rounded-sm w-10 h-10 flex items-center justify-center mr-4 text-lg font-black">4</span>
              Qué Hacer Paso a Paso
            </h2>
            
            <div className="space-y-4">
              {[
                { step: 1, title: "Confirma que es la batería", desc: "Intenta arrancar: si oyes un clic rápido o nada, es la batería." },
                { step: 2, title: "Llámanos al 613 076 099", desc: "O escríbenos por WhatsApp. Te preguntaremos tu ubicación exacta." },
                { step: 3, title: "Espera en lugar seguro", desc: "Llegamos en menos de 30 minutos a cualquier punto de Barcelona." },
                { step: 4, title: "Arrancamos tu coche", desc: "Usamos batería portátil profesional. Solo pagas si funciona." },
                { step: 5, title: "Guarda la factura", desc: "Te la enviamos por email para que reclames a la empresa de alquiler." }
              ].map((item) => (
                <div key={item.step} className="flex items-start space-x-4 bg-obsidian border border-white/10 rounded-sm p-4">
                  <div className="bg-white/10 text-white rounded-sm w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">
                    {item.step}
                  </div>
                  <div>
                    <h4 className="font-bold text-white">{item.title}</h4>
                    <p className="text-white/60 text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Sección 5: Empresas de alquiler */}
          <section className="bg-white/5 border border-white/10 rounded-sm p-8">
            <h2 className="font-heading text-2xl font-bold text-white mb-6 flex items-center">
              <span className="bg-neon text-obsidian rounded-sm w-10 h-10 flex items-center justify-center mr-4 text-lg font-black">5</span>
              ¿Qué Dicen las Empresas de Alquiler?
            </h2>
            
            <div className="bg-white/5 border border-white/10 rounded-sm p-6 mb-6">
              <p className="text-white/80 leading-relaxed">
                La mayoría de empresas de alquiler (Europcar, Hertz, Avis, Sixt, Goldcar, etc.) 
                <strong className="text-white"> aceptan reembolsar gastos de asistencia</strong> si la avería no fue culpa del cliente.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-neon/10 border border-neon/30 rounded-sm p-4">
                <h4 className="font-bold text-neon mb-2 flex items-center">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Para conseguir el reembolso:
                </h4>
                <ul className="text-white/70 text-sm space-y-1">
                  <li>• Guarda la factura original</li>
                  <li>• Haz fotos del panel del coche</li>
                  <li>• Anota la hora y ubicación</li>
                  <li>• Informa al devolver el coche</li>
                </ul>
              </div>
              <div className="bg-stark/10 border border-stark/30 rounded-sm p-4">
                <h4 className="font-bold text-stark mb-2 flex items-center">
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Evita problemas:
                </h4>
                <ul className="text-white/70 text-sm space-y-1">
                  <li>• No intentes arrancar con cables tú mismo</li>
                  <li>• No uses servicios sin factura</li>
                  <li>• No aceptes "arreglos" informales</li>
                </ul>
              </div>
            </div>
          </section>

          {/* FAQ específico */}
          <section className="bg-white/5 border border-white/10 rounded-sm p-8">
            <h2 className="font-heading text-2xl font-bold text-white mb-6">
              Preguntas Frecuentes: Coche de Alquiler
            </h2>
            
            <div className="space-y-4">
              {[
                { 
                  q: "¿La empresa de alquiler me cobrará si uso un servicio externo?", 
                  a: "No. Si no fue tu culpa (dejaste luces encendidas, etc.) y tienes factura, generalmente reembolsan. Si fue tu error, depende de tu seguro." 
                },
                { 
                  q: "¿Puedo reclamar a mi seguro de viaje?", 
                  a: "Sí. Muchos seguros de viaje y tarjetas de crédito premium cubren asistencia en carretera. Consulta tu póliza." 
                },
                { 
                  q: "¿Funciona con cualquier marca de coche?", 
                  a: "Sí. Nuestro equipo profesional funciona con todas las marcas de coches de alquiler: Seat, Volkswagen, Peugeot, Renault, etc." 
                },
                { 
                  q: "¿Debo devolver el coche antes por esto?", 
                  a: "No necesariamente. Una vez arrancado, conduce al menos 20-30 minutos para recargar la batería y podrás seguir usándolo normalmente." 
                }
              ].map((item, idx) => (
                <div key={idx} className="bg-obsidian border border-white/10 rounded-sm p-4">
                  <h4 className="font-bold text-white mb-2">{item.q}</h4>
                  <p className="text-white/60 text-sm">{item.a}</p>
                </div>
              ))}
            </div>
          </section>

        </div>

        {/* CTA */}
        <div className="bg-neon text-obsidian rounded-sm p-8 mt-12">
          <div className="text-center">
            <h2 className="font-heading text-2xl font-black mb-4">¿Tu Coche de Alquiler No Arranca?</h2>
            <p className="text-obsidian/80 mb-6 max-w-xl mx-auto">
              Llámanos ahora y llegaremos en menos de 30 minutos. Servicio profesional con factura para tu reclamación.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/')}
                className="bg-obsidian hover:bg-obsidian/90 text-white px-8 py-3 rounded-sm font-bold"
                data-testid="cta-whatsapp-btn"
              >
                <Zap className="h-5 w-5 mr-2" />
                Solicitar Arranque
              </Button>
              <Button
                onClick={() => window.open('tel:+34613076099')}
                variant="outline"
                className="border-2 border-obsidian text-obsidian hover:bg-obsidian hover:text-white px-8 py-3 rounded-sm font-bold"
                data-testid="cta-phone-btn"
              >
                <Phone className="h-5 w-5 mr-2" />
                613 076 099
              </Button>
            </div>
            <p className="text-obsidian/60 text-sm mt-4">
              Servicio 24/7 • Precio fijo 69€/99€ • Factura incluida
            </p>
          </div>
        </div>
      </article>
    </div>
  );
};

export default BateriaDescargadaCocheAlquiler;
